import { createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux"
import { CreatableMaintenanceTemplate, MaintenanceTemplate } from "@nova/schemas"

type ConditionState = {
    enabled: boolean
    value: number | null
}

export type MaintenanceTemplateFormState = {
    name: string
    description: string
    usage: ConditionState
    duration: ConditionState
    interval: ConditionState
    instructions: string
    statusOnTrigger: string
    aircraftIds: string[]
    modelTargets: string[]
    applyToAll: boolean
}

type MaintenanceTemplateFormSliceState = {
    form: MaintenanceTemplateFormState
    submitting: boolean
}

const initialState: MaintenanceTemplateFormSliceState = {
    form: {
        name: "",
        description: "",
        usage: { enabled: true, value: 20 },
        duration: { enabled: true, value: 200 },
        interval: { enabled: true, value: 180 },
        instructions: "",
        statusOnTrigger: "needs_maintenance",
        aircraftIds: [],
        modelTargets: [],
        applyToAll: false,
    },
    submitting: false,
}

export const maintenanceTemplateFormSliceName = "maintenanceTemplateForm" as const

export const {
    actions: { updateForm, resetForm, loadTemplate, setSubmitting },
    reducer: maintenanceTemplateFormReducer,
} = createSlice({
    name: maintenanceTemplateFormSliceName,
    initialState,
    reducers: {
        updateForm: <K extends keyof MaintenanceTemplateFormState>(
            state: MaintenanceTemplateFormSliceState,
            action: { payload: { key: K; value: MaintenanceTemplateFormState[K] } }
        ) => {
            state.form[action.payload.key] = action.payload.value
        },
        resetForm: () => initialState,
        loadTemplate: (
            _state: MaintenanceTemplateFormSliceState,
            action: { payload: { template: MaintenanceTemplate; aircraftIds: string[] } }
        ) => {
            const { template, aircraftIds } = action.payload
            return {
                form: {
                    name: template.name,
                    description: template.description ?? "",
                    instructions: template.instructions ?? "",
                    statusOnTrigger: template.statusOnTrigger,
                    aircraftIds,
                    modelTargets: template.modelTargets,
                    applyToAll: template.applyToAll,
                    usage: {
                        enabled: template.conditionUsageFlights != null,
                        value: template.conditionUsageFlights,
                    },
                    duration: {
                        enabled: template.conditionDurationHours != null,
                        value: template.conditionDurationHours,
                    },
                    interval: {
                        enabled: template.conditionIntervalValue != null,
                        value: template.conditionIntervalValue,
                    },
                },
                submitting: false,
            }
        },
        setSubmitting: (
            state,
            action: { payload: MaintenanceTemplateFormSliceState["submitting"] }
        ) => {
            state.submitting = action.payload
        },
    },
})

const root = (state: RootState) => state[maintenanceTemplateFormSliceName]

export const selectForm = createSelector([root], (s) => s.form)

export const selectFormAsCreatable = createSelector(
    [selectForm],
    (form): CreatableMaintenanceTemplate => ({
        name: form.name,
        description: form.description || null,
        instructions: form.instructions || null,
        condition_usage_flights: form.usage.enabled ? form.usage.value : null,
        condition_duration_hours: form.duration.enabled ? form.duration.value : null,
        condition_interval_value: form.interval.enabled ? form.interval.value : null,
        status_on_trigger: form.statusOnTrigger,
        apply_to_all: form.applyToAll,
        aircraft_ids: form.applyToAll ? [] : form.aircraftIds,
        model_targets: form.applyToAll ? [] : form.modelTargets,
    })
)
export const selectSubmitting = createSelector([root], (s) => s.submitting)
